import { useQuery, QueryClient } from '@tanstack/react-query';

import { referenceDataMapping } from '../../mapping/referenceDataMapping.js';

//** Axios request */
import { request } from '../../utilities/axios-utils.js';
import { isObjEmpty } from '../../utilities/utils.js';
import useSharedData from '../useSharedData.ts';

import { queryKeys } from './queryKeys.js';

//----------------------------------------------------------------

const queryClient = new QueryClient();

async function loadReferenceData(isDev) {
  if (isDev === true && import.meta.env.MODE === 'localdev') {
    try {
      const module = await import('../../data/mock/referencedata-mock.js');

      // named export
      const getReferenceData = module.getReferenceData;

      if (typeof getReferenceData !== 'function') {
        throw new Error('getReferenceData export not found (or not a function)');
      }

      const mockResponse = getReferenceData(); // ✅ call it
      const returnResponse = await referenceDataMapping(mockResponse.data); // ✅ use returned object

      return returnResponse;
    } catch (error) {
      console.log('Error loading mock data: ', error);
    }

    return undefined; // or whatever your non-localdev path returns
  }
}

const fetchReferenceData = async (openErrorModal, pdsUrl, setPdsUrl, isDevShared) => {
  const cachedRefData = queryClient.getQueryData([queryKeys.REFERENCE_DATA_FETCH]);

  const isDev = isDevShared;

  if (isDev === true && import.meta.env.MODE === 'localdev') {
    return loadReferenceData(true);
  }

  if (!isObjEmpty(cachedRefData)) {
    return cachedRefData;
  }

  const response = await request({
    url: '/getReferenceData',
    method: 'GET',
  });
  if (200 !== response.status) {
    openErrorModal();
    throw response;
  } else {
    if (!pdsUrl) {
      // Only set if it wasn't set before
      await setPdsUrl(response?.data?.pdsUrl);
    }
    let returnResponse = await referenceDataMapping(response.data);
    return returnResponse;
  }
};

export const useGetReferenceData = () => {
  const { openErrorModal, pdsUrl, setPdsUrl, isDevShared } = useSharedData();

  return useQuery({
    queryKey: [queryKeys.REFERENCE_DATA_FETCH],
    queryFn: async () => await fetchReferenceData(openErrorModal, pdsUrl, setPdsUrl, isDevShared),
    refetchOnWindowFocus: false,
    cacheTime: 15 * (60 * 1000),
    staleTime: Infinity,
  });
};
